import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Symbol, Basket, PredictionModel } from './types';
import { BasketManager } from './BasketManager';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export interface SymbolSelectionProps {
  symbols: Symbol[];
  symbolInput: string;
  symbolDirection: 'long' | 'short' | 'auto';
  onSymbolInputChange?: (value: string) => void;
  onSymbolDirectionChange?: (value: 'long' | 'short' | 'auto') => void;
  onAddSymbol: (symbol: string, direction: 'long' | 'short' | 'auto', symbolData?: Partial<Symbol>) => void;
  onRemoveSymbol: (symbolToRemove: string) => void;
  onBasketSelect: (basket: Basket) => void;
  baskets?: Basket[];
  onAddBasket?: () => void;
  onUpdateBasket?: (basketId: string, updates: Partial<Basket>) => void;
  onRemoveBasket?: (basketId: string) => void;
  setSymbolInput?: (value: string) => void;
  setSymbolDirection?: (value: 'long' | 'short' | 'auto') => void;
  predictionModels?: PredictionModel[];
}

export const SymbolSelection: React.FC<SymbolSelectionProps> = ({
  symbols,
  symbolInput,
  symbolDirection,
  onSymbolInputChange,
  onSymbolDirectionChange,
  onAddSymbol,
  onRemoveSymbol,
  onBasketSelect,
  baskets = [],
  onAddBasket,
  onUpdateBasket,
  onRemoveBasket,
  setSymbolInput,
  setSymbolDirection,
  predictionModels = []
}) => {
  const [activeTab, setActiveTab] = useState<'symbols' | 'baskets'>('symbols');
  const basketsContainerRef = useRef<HTMLDivElement>(null);
  
  // Handle tab change and scroll to bottom when switching to baskets tab
  const handleTabChange = (tab: 'symbols' | 'baskets') => {
    // Clear conflicting data when switching tabs
    if (tab === 'baskets' && symbols.length > 0) {
      // Ask user to confirm clearing individual symbols when switching to baskets
      const confirmClear = window.confirm(
        `You have ${symbols.length} individual symbol(s) selected. Switching to baskets will clear these symbols. Continue?`
      );
      if (confirmClear) {
        // Clear all individual symbols
        symbols.forEach(symbol => onRemoveSymbol(symbol.ticker || symbol.symbol || ''));
      } else {
        return; // Don't switch tabs if user cancels
      }
    } else if (tab === 'symbols' && baskets && baskets.length > 0 && baskets.some(b => b.symbols.length > 0)) {
      // Ask user to confirm clearing baskets when switching to individual symbols
      const basketCount = baskets.filter(b => b.symbols.length > 0).length;
      const confirmClear = window.confirm(
        `You have ${basketCount} basket(s) configured. Switching to individual symbols will clear these baskets. Continue?`
      );
      if (confirmClear && onRemoveBasket) {
        // Clear all baskets
        baskets.forEach(basket => onRemoveBasket(basket.id));
      } else {
        return; // Don't switch tabs if user cancels
      }
    }

    setActiveTab(tab);
    
    // When switching to baskets tab, scroll to the appropriate position
    if (tab === 'baskets') {
      // Use a short timeout to ensure the DOM has updated
      setTimeout(() => {
        if (basketsContainerRef.current) {
          // If no baskets, scroll to see the "Create First Basket" button
          if (baskets.length === 0) {
            const emptyStateCard = basketsContainerRef.current.querySelector('.border-dashed');
            if (emptyStateCard) {
              emptyStateCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          } else {
            // If baskets exist, scroll to make the "Add Basket" button visible
            const addBasketBtn = basketsContainerRef.current.querySelector('button');
            if (addBasketBtn) {
              basketsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }
      }, 150); // Slightly longer timeout for more reliability
    }
  };
  
  const getDirectionIcon = (direction: string) => {
    if (direction === 'short') {
      return <ArrowDown className="h-4 w-4 text-red-500" />;
    } else if (direction === 'auto') {
      return <ArrowUpDown className="h-4 w-4 text-purple-500" />;
    } else {
      return <ArrowUp className="h-4 w-4 text-green-500" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Custom tabs implementation without using Tabs components */}
      <div className="w-full">
        <div className="grid grid-cols-2 mb-4 border rounded-lg overflow-hidden">
          <button
            onClick={() => handleTabChange('symbols')}
            className={cn(
              "py-2 text-center text-sm font-medium transition-colors",
              activeTab === 'symbols' 
                ? "bg-primary text-primary-foreground" 
                : "hover:bg-muted/50"
            )}
          >
            Individual Symbols
          </button>
          <button
            onClick={() => handleTabChange('baskets')}
            className={cn(
              "py-2 text-center text-sm font-medium transition-colors",
              activeTab === 'baskets' 
                ? "bg-primary text-primary-foreground" 
                : "hover:bg-muted/50"
            )}
          >
            Symbol Baskets
          </button>
        </div>
        
        {activeTab === 'symbols' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-medium mb-2">Add Symbols</h3>
              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Enter symbol"
                  value={symbolInput}
                  onChange={(e) => {
                    if (onSymbolInputChange) {
                      onSymbolInputChange(e.target.value);
                    } else if (setSymbolInput) {
                      setSymbolInput(e.target.value);
                    }
                  }}
                  className="flex-1"
                />
                <Select 
                  value={symbolDirection} 
                  onValueChange={(val) => {
                    if (onSymbolDirectionChange) {
                      onSymbolDirectionChange(val as 'long' | 'short' | 'auto');
                    } else if (setSymbolDirection) {
                      setSymbolDirection(val as 'long' | 'short' | 'auto');
                    }
                  }}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="long">Long</SelectItem>
                    <SelectItem value="short">Short</SelectItem>
                    <SelectItem value="auto">Auto</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onAddSymbol(symbolInput, symbolDirection, {
                    // Use default global profit target and stop loss values
                    profitTarget: '10',
                    stopLoss: '2'
                  })}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-base font-medium mb-2">Selected Symbols</h3>
              <div className="flex flex-wrap gap-2">
                {symbols.map((symbol, index) => (
                  <div
                    key={`${symbol.ticker || symbol.symbol}-${index}`}
                    className="flex items-center gap-2 p-2 rounded-md border border-gray-200 mb-2"
                  >
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      symbol.direction === 'auto'
                        ? 'bg-blue-100 text-blue-700'
                        : symbol.direction === 'long'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {symbol.ticker || symbol.symbol}
                      {getDirectionIcon(symbol.direction)}
                    </div>
                    
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex items-center">
                        <label className="text-xs mr-1">Profit:</label>
                        <Input 
                          type="number"
                          min="0"
                          step="0.1"
                          className="h-6 w-16 text-xs"
                          value={symbol.profitTarget || ''}
                          onChange={(e) => {
                            const updatedSymbol = {...symbol, profitTarget: e.target.value};
                            // Update symbol by removing and re-adding with new values
                            onRemoveSymbol(symbol.ticker || symbol.symbol || '');
                            onAddSymbol(
                              symbol.ticker || symbol.symbol || '', 
                              symbol.direction,
                              updatedSymbol
                            );
                          }}
                        />
                        <span className="text-xs ml-1">%</span>
                      </div>
                      
                      <div className="flex items-center">
                        <label className="text-xs mr-1">Stop:</label>
                        <Input 
                          type="number"
                          min="0"
                          step="0.1"
                          className="h-6 w-16 text-xs"
                          value={symbol.stopLoss || ''}
                          onChange={(e) => {
                            const updatedSymbol = {...symbol, stopLoss: e.target.value};
                            // Update symbol by removing and re-adding with new values
                            onRemoveSymbol(symbol.ticker || symbol.symbol || '');
                            onAddSymbol(
                              symbol.ticker || symbol.symbol || '', 
                              symbol.direction,
                              updatedSymbol
                            );
                          }}
                        />
                        <span className="text-xs ml-1">%</span>
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveSymbol(symbol.ticker || symbol.symbol || '')}
                      className="rounded-full hover:bg-gray-200 h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                
                {symbols.length === 0 && (
                  <div className="text-muted-foreground text-sm italic p-4 border border-dashed rounded-md text-center w-full">
                    No symbols added yet. Add symbols above or create symbol baskets.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'baskets' && onAddBasket && onUpdateBasket && onRemoveBasket && (
          <div ref={basketsContainerRef}>
            <BasketManager
              baskets={baskets}
              onAddBasket={onAddBasket}
              onUpdateBasket={onUpdateBasket}
              onRemoveBasket={onRemoveBasket}
              predictionModels={predictionModels}
            />
          </div>
        )}
      </div>
    </div>
  );
};
